import { metricNumberFormat } from '../presentation';
import type {
  KpiAdapterConfig,
  KpiAdapterProps,
  KpiCoreProps,
  RenderAdapterResult,
} from '../types';
import { validateRowsInput } from '../validateRowsInput';
import type { TransformRow } from '../../transforms/types';

/** Resolves a dotted path (`a.b.c`) against a row. */
function readPath(row: TransformRow, path: string): unknown {
  return path.split('.').reduce<unknown>((value, key) => {
    if (value != null && typeof value === 'object') {
      return (value as Record<string, unknown>)[key];
    }
    return undefined;
  }, row);
}

/**
 * The row this tile is about.
 *
 * A long-form response carries one row per dimension value, so a tile showing
 * "applications, Fall 2026" has to find its own row among rows that all look
 * alike. Wide-form and single-row responses author no selector and take the
 * first row.
 */
function selectRow(
  rows: readonly TransformRow[],
  rowSelector: KpiAdapterConfig['rowSelector'],
): TransformRow | undefined {
  const keys = rowSelector ? Object.keys(rowSelector) : [];
  if (keys.length === 0) {
    return rows[0];
  }
  return rows.find((row) =>
    keys.every((key) => row[key] === rowSelector![key]),
  );
}

/** A measure that arrived as text is still a number; anything else is not. */
function asNumber(raw: unknown): number | null {
  if (typeof raw === 'number') {
    return Number.isFinite(raw) ? raw : null;
  }
  if (typeof raw === 'string' && raw.trim() !== '') {
    const coerced = Number(raw);
    return Number.isFinite(coerced) ? coerced : null;
  }
  return null;
}

/**
 * Points for the sparkline, dropping rows missing either coordinate.
 *
 * A point with no y would render at the axis, which reads as a real zero, and a
 * point with no x would be labeled by its position instead of its period.
 *
 * y goes through the same coercion as the headline figure. Reporting rows
 * routinely carry measures as text, and requiring a `number` here while the
 * headline accepts `'1234'` empties the series for exactly the tiles that do
 * have a figure to draw — the renderer then falls back to a big number, which
 * looks like an authoring choice rather than dropped data. x is deliberately
 * not coerced: its contract is `string | number` because a period label like
 * `'Fall 2024'` is a legitimate x, and narrowing it to a number would relabel
 * axes by ordinal instead.
 */
function trendPoints(
  rows: readonly TransformRow[],
  xPath: string,
  yPath: string,
): KpiCoreProps['trendData'] {
  return rows
    .map((row) => ({
      x: readPath(row, xPath),
      y: asNumber(readPath(row, yPath)),
    }))
    .filter(
      (point): point is { x: string | number; y: number } =>
        (typeof point.x === 'string' || typeof point.x === 'number') &&
        point.y !== null,
    );
}

function kpiProps(
  config: KpiAdapterConfig | undefined,
  rows: readonly TransformRow[],
): KpiAdapterProps {
  const presentation = config?.presentation;
  const isStatic = presentation?.staticValue !== undefined;
  const row = isStatic ? rows[0] : selectRow(rows, config?.rowSelector);

  const rawValue =
    !isStatic && config?.valuePath
      ? readPath(row ?? {}, config.valuePath)
      : null;
  const rawContext =
    !isStatic && config?.contextPath && row
      ? readPath(row, config.contextPath)
      : null;

  // `'raw'` means print what is there, including values that are not numbers,
  // so it resolves to text. Every other format describes a number and is left
  // for the renderer to apply — the tile needs the number anyway, to color
  // itself by the value's own sign.
  const value = isStatic
    ? presentation.staticValue!
    : config?.format === 'raw'
      ? rawValue == null
        ? { value: null }
        : String(rawValue)
      : {
          value: asNumber(rawValue),
          format: metricNumberFormat(config?.format),
        };

  const delta = isStatic ? null : asNumber(rawContext);

  return {
    ...presentation?.passthrough,
    label: presentation?.label ?? '',
    value,
    delta,
    directionality: presentation?.directionality ?? 'higher-is-better',
    // A tile with no comparison column authored shows no delta row at all,
    // which is different from one whose comparison came back empty.
    deltaLabel:
      !isStatic && config?.contextPath
        ? { value: delta, format: metricNumberFormat(config.contextFormat) }
        : undefined,
    deltaSubLabel: isStatic ? undefined : presentation?.deltaSubLabel,
    colorValue: presentation?.colorValue,
    // A static figure has no series behind it, so it cannot draw a trend.
    statStyle: isStatic ? 'big-number' : presentation?.statStyle,
    trendData:
      !isStatic &&
      presentation?.statStyle === 'sparkline' &&
      config?.trendXPath &&
      config?.trendValuePath
        ? trendPoints(rows, config.trendXPath, config.trendValuePath)
        : undefined,
  };
}

/**
 * One reporting result to one KPI tile.
 *
 * Emits the figure as a number plus its format rather than as text. The tile
 * renders the text and reads the number for `colorValue: 'auto-by-value'`,
 * which is the same reason the props stay serializable: formatting early would
 * force this layer to be the one that knows how a percent looks.
 */
export function kpiAdapter(
  rows: unknown,
  config: KpiAdapterConfig,
): RenderAdapterResult<KpiAdapterProps> {
  const validated = validateRowsInput(rows, kpiProps(config, []));
  if ('props' in validated) {
    return validated;
  }

  return { props: kpiProps(config, validated.rows) };
}
